import { useEffect, useMemo, useRef, useState } from "react";
import {
  formatCurrency,
  formatEasyDate,
  rgbaObjectToString,
  transactionDescription,
} from "../../helpers/utils";
import useHistoryStore from "../../store/historyStore";
import { isString, isObject } from "lodash-es";

import clsx from "clsx";
import {
  ETransactionType,
  FILE_STORAGE_SERVER_URL,
  ICON_SIZES,
} from "../../helpers/constants";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import CommonTable from "../../components/Tables/CommonTable";
import useStatuses from "../../hooks/useStatuses";
import useColors from "../../hooks/useColors";
import {
  MdAttachment,
  MdOutlineFolder,
  MdOutlineFolderOpen,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ICellRendererParams } from "ag-grid-community";
import usePinnedColumns from "src/hooks/usePinnedColumns";
import TransactionAttachmentsLightbox from "../NewTransaction/TransactionAttachmentsLightbox";
import useAllTransactions from "src/hooks/useAllTransactions";
import ClientProviderInfoModal from "src/components/ClientProviderInfoModal/ClientProviderInfoModal";
import useCurrentUser from "src/hooks/useCurrentUser";
import {
  EAttachmentsTableKeys,
  ECategoriesTableKeys,
  EContactInvitesTableKeys,
  EPaymentMethodsTableKeys,
  EReportsTableKeys,
  ESourcesTableKeys,
  ETransactionsTableKeys,
} from "src/components/Tables/tableIds";

export interface ITableSelection {
  allSelected: boolean;
  selectedCount: number;
  selectedRows: Array<any>;
}

interface ITransactionsTable {
  transactions: Array<any>;
  isLoading?: boolean;
  onSelectionChange?: (rows: Array<any>) => void;
  settingsKey: ETransactionsTableKeys;
  singleSelection?: boolean;
}

const TransactionsTable: React.FC<ITransactionsTable> = ({
  transactions = [],
  isLoading = false,
  onSelectionChange = null,
  settingsKey,
  singleSelection = false,
}) => {
  const { allPaymentMethods, getPaymentMethodById } = useCurrentUser();
  const { currencySymbol } = useCurrentAccount();
  const { statuses } = useStatuses();
  const { updatePinnedColumns, isPinnedColumn } = usePinnedColumns();
  const { colors } = useColors();
  const navigate = useNavigate();
  const thumbnailsRef = useRef<any>(null);
  const { getCategoryCustomSettings, getSourceCustomSettings } =
    useAllTransactions();
  const [showSourceInfo, setShowSourceInfo] = useState<boolean>(false);
  const [sourceToShowDetailsOf, setSourceToShowDetailsOf] =
    useState<string>("");

  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);
  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);

  const { isTransactionInHistory, markTransactionAsOpened, historyIsEnabled } =
    useHistoryStore();

  const [selectedRows, setSelectedRows] = useState<Array<any>>([]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showSourceInfo) setSourceToShowDetailsOf("");
  }, [showSourceInfo]);

  const [transactionAttachments, setTransactionAttachments] = useState<any[]>(
    []
  );

  const formattedAttachmentsPerVersion = useMemo(() => {
    if (transactionAttachments?.length) {
      const [firstAttachment] = transactionAttachments;

      if (isString(firstAttachment)) {
        return transactionAttachments.map?.((simpleStringAttachment) => ({
          fileUrl: `${FILE_STORAGE_SERVER_URL}/${simpleStringAttachment}`,
          originalName: simpleStringAttachment,
          size: "N/A",
          storedFileName: simpleStringAttachment,
          thumbnailFileName: simpleStringAttachment,
          thumbnailUrl: `${FILE_STORAGE_SERVER_URL}/${simpleStringAttachment}`,
          type: "N/A",
          uploaderVersion: "v1",
        }));
      } else if (isObject(firstAttachment)) {
        return transactionAttachments;
      }
    }
    return [];
  }, [transactionAttachments]);

  const transactionSlides: any[] = useMemo(() => {
    return (
      formattedAttachmentsPerVersion?.map?.((attachment: any) => {
        return {
          type: attachment?.type === "application/pdf" ? undefined : "image",
          src: attachment?.fileUrl,
        };
      }) || []
    );
  }, [formattedAttachmentsPerVersion]);

  const columns: any = useMemo(
    () => [
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "date",
        floatingFilter: false,
        headerName: "FECHA",
        pinned: isPinnedColumn("transactionsDatePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsDatePinned: isPinned,
              });
            },
          },
        },
        filterParams: {
          comparator: (filterDate: any, cellValue: any) => {
            const cellDate = new Date(cellValue);
            return cellDate.getTime() - filterDate.getTime();
          },
          filterPlaceholder: "Filtrar...",
        },
        filter: "agDateColumnFilter",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: any) => {
          return {
            params: {
              ...params,
              value: formatEasyDate({ dateToFormat: params.value }),
            },
            custom: {
              background: "#555",
              color: "#fff",
              className: "text-right",
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        // minWidth: 150,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "payTo",
        floatingFilter: false,
        filter: "agTextColumnFilter",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        headerName: "ORIGEN / DESTINO",
        pinned: isPinnedColumn("transactionsClientProviderPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsClientProviderPinned: isPinned,
              });
            },
          },
        },
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const info = getSourceCustomSettings(params?.value);

          return {
            params,
            custom: {
              background: rgbaObjectToString(info?.bg),
              color: rgbaObjectToString(info?.color),
              className: "text-right",
              onClick: (e: any) => {
                setSourceToShowDetailsOf(params?.value);
                setShowSourceInfo(true);
              },
            },
          };
        },
        // minWidth: 200,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "total",
        floatingFilter: false,
        headerName: "TOTAL",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        cellRenderer: "CellWithBackgroundRenderer",
        filter: "agNumberColumnFilter",
        pinned: isPinnedColumn("transactionsTotalPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsTotalPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: ICellRendererParams) => {
          const isIncome = params?.data?.type === ETransactionType.INCOME;

          return {
            params: {
              ...params,
              value: formatCurrency({
                value: params?.data?.total,
                currency: currencySymbol,
              }),
            },
            custom: {
              background: isIncome ? colors.income.bg : colors.expenses.bg,
              color: isIncome ? colors.income.color : colors.expenses.color,
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        // minWidth: 170,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "description",
        valueFormatter: (params: any) => {
          return transactionDescription(params?.data);
        },
        floatingFilter: false,
        filter: "agTextColumnFilter",
        headerName: "DESCRIPCIÓN",
        // minWidth: 200,
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        pinned: isPinnedColumn("transactionsDescriptionPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsDescriptionPinned: isPinned,
              });
            },
          },
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div
              onClick={() => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              }}
              className={clsx([
                "hover:underline",
                "hover:cursor-pointer",
                "inline",
              ])}
            >
              {params?.value}
            </div>
          );
        },
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "category",
        floatingFilter: false,
        filter: "agTextColumnFilter",
        headerName: "CATEGORÍA",
        // minWidth: 200,
        cellRenderer: "CellWithBackgroundRenderer",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        pinned: isPinnedColumn("transactionsCategoryPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsCategoryPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: ICellRendererParams) => {
          const cat = getCategoryCustomSettings(params?.value);

          return {
            params: {
              ...params,
              value: params?.value || "Sin categoría",
            },
            custom: {
              background: rgbaObjectToString(cat?.bg),
              color: rgbaObjectToString(cat?.color),
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
      },

      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "paymentMethod",
        floatingFilter: false,
        filter: "agTextColumnFilter",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        pinned: isPinnedColumn("transactionsPaymentPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsPaymentPinned: isPinned,
              });
            },
          },
        },
        headerName: "MÉTODO DE PAGO",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const methodData = getPaymentMethodById(params?.value);
          const colors = {
            background: "#555",
            color: "#fff",
          };

          if (methodData?.settings) {
            colors.background = rgbaObjectToString(methodData?.settings?.bg);
            colors.color = rgbaObjectToString(methodData?.settings?.color);
          }

          return {
            params: {
              value: methodData?.name || params.value || "Desconocido",
            },
            custom: {
              ...colors,
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        // minWidth: 180,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        field: "status",
        floatingFilter: false,
        headerName: "ESTADO",
        cellRenderer: "CellWithBackgroundRenderer",
        filter: "agTextColumnFilter",
        pinned: isPinnedColumn("transactionsStatusPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsStatusPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: ICellRendererParams) => {
          const statusData = statuses?.[params?.data?.status] || {};

          return {
            params: {
              ...params,
              value: statusData?.name || "Desconocido",
            },
            custom: {
              background: rgbaObjectToString(
                statusData?.settings?.bg || { r: 0, g: 0, b: 0, a: 1 }
              ),
              color: rgbaObjectToString(
                statusData?.settings?.color || { r: 255, g: 255, b: 255, a: 1 }
              ),
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        // minWidth: 150,
      },
      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ARCHIVOS",
        // minWidth: 80,
        width: 150,
        pinned: isPinnedColumn("transactionsActionsPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionsActionsPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: any) => {
          const alreadyOpened =
            historyIsEnabled && isTransactionInHistory(params.data.invoice);
          const attachments = params?.data?.attachments;

          return {
            buttons: [
              {
                icon: <MdAttachment size={ICON_SIZES.SM} />,
                onClick: () => {
                  setTransactionAttachments(attachments);
                  setClickedAttachmentIndex(0);
                  setAttachmentsGalleryIsOpen(true);
                },
                text: `${attachments?.length}`,
                disabled: !attachments?.length,
                // hide: !attachments?.length,
                className: clsx([
                  "!min-h-[40px]",
                  "!min-w-[40px]",
                  "!max-h-[40px]",
                  "!max-w-[60px]",
                  "!text-xs md:!text-xs",
                ]),
              },
              {
                hide: true,
                icon: alreadyOpened ? (
                  <MdOutlineFolderOpen size={ICON_SIZES.SM} />
                ) : (
                  <MdOutlineFolder size={ICON_SIZES.SM} />
                ),
                onClick: () => {
                  const { data } = params;

                  if (
                    historyIsEnabled &&
                    !isTransactionInHistory(data.invoice)
                  ) {
                    markTransactionAsOpened(data.invoice);
                  }

                  navigate(`/transaction/${data?.account}/${data?.invoice}`);
                },
                className: clsx([
                  "!min-h-[40px]",
                  "!min-w-[40px]",
                  "!max-h-[40px]",
                  "!max-w-[40px]",
                ]),
              },
            ],
          };
        },
      },
    ],
    [
      colors?.expenses?.color,
      colors?.income?.color,
      colors?.expenses?.bg,
      colors?.income?.bg,
      currencySymbol,
      statuses,
      historyIsEnabled,
      getCategoryCustomSettings,
      isPinnedColumn,
    ]
  );

  return (
    <>
      <div className={clsx(["flex", "flex-col", "gap-2", "rounded-sm"])}>
        <CommonTable
          columns={columns}
          data={transactions}
          onSelectionChange={setSelectedRows}
          settingsKey={settingsKey}
          singleSelection={singleSelection}
        />
      </div>
      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={transactionSlides}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          thumbnailsRef={thumbnailsRef}
          unifiedAttachmentsList={formattedAttachmentsPerVersion}
          onClose={() => {
            setAttachmentsGalleryIsOpen(false);
            setClickedAttachmentIndex(0);
            setTransactionAttachments([]);
          }}
        />
      )}
      {!!(showSourceInfo && !!sourceToShowDetailsOf) && (
        <ClientProviderInfoModal
          onClose={() => setShowSourceInfo(false)}
          clientProvider={sourceToShowDetailsOf}
        />
      )}
    </>
  );
};

export default TransactionsTable;
